import React from "react";
import Flx from "../../../components/layout/Flx";
import PublicFormGrid from "../../../components/publicForm/PublicFormGrid";
import PublicFormSection from "../../../components/publicForm/PublicFormSection";
import RffAddressEditingField from "../../../components/finalForm/inputs/RffAddressEditingField";
import RffBooleanToggleField from "../../../components/finalForm/inputs/RffBooleanToggleField";
import RffDateField from "../../../components/finalForm/inputs/RffDateField";
import RffDateOfBirthField from "../../../components/finalForm/inputs/RffDateOfBirthField";
import RffEmailField from "../../../components/finalForm/inputs/RffEmailField";
import RffExitStrategyField from "../../../components/finalForm/inputs/RffExitStrategyField";
import RffFileUploadField from "../../../components/finalForm/inputs/RffFileUploadField";
import RffPhoneField from "../../../components/finalForm/inputs/RffPhoneField";
import RffSelectToggleField from "../../../components/finalForm/inputs/RffSelectToggleField";
import RffSsnField from "../../../components/finalForm/inputs/RffSsnField";
import RffTextField from "../../../components/finalForm/inputs/RffTextField";
import RffConditional from "../../../components/finalForm/shared/RffConditional";
import { selectOptionsLoanProductType } from "../../../constants/selectOptions/selectOptionsLoanProductType";
import { selectOptionsLoanPurpose } from "../../../constants/selectOptions/selectOptionsLoanPurpose";
import { selectOptionsMaritalStatus } from "../../../constants/selectOptions/selectOptionsMaritalStatus";
import { CITIZENSHIP_OPTIONS } from "../applicationFormShared";

/**
 * Step 1 — who the applicant is, what they're borrowing for, and proof of ID.
 *
 * The loan basics live here rather than on the Loan Details step because
 * `loanProductType` and `loanPurpose` gate conditionals on every later step —
 * asking them first means the rest of the form is already tailored by the time
 * the applicant reaches it. The amount fields stay on Loan Details.
 */
const StepBorrowerDetails = () => (
  <Flx column>
    <PublicFormSection title="Contact Information">
      <PublicFormGrid>
        <RffTextField required name="firstName" label="First Name" size={6} />
        <RffTextField required name="lastName" label="Last Name" size={6} />
        <RffPhoneField required name="phone" label="Phone" size={6} />
        <RffEmailField required name="emailAddress" label="Email" size={6} />
      </PublicFormGrid>
    </PublicFormSection>

    <PublicFormSection title="Citizenship &amp; Identity">
      <PublicFormGrid>
        <RffSelectToggleField
          required
          name="citizenshipStatus"
          label="Please select your citizenship category"
          options={CITIZENSHIP_OPTIONS}
        />
        <RffAddressEditingField
          required
          name="primaryAddress"
          label="Primary Address"
        />
        {/* No `size`: nothing shares this row, so it takes the full width. Only
            pair a field down to size={6} when something sits beside it. */}
        <RffDateOfBirthField
          required
          name="dateOfBirth"
          label="Date of birth"
        />
        <RffSelectToggleField
          required
          size={6}
          name="maritalStatus"
          label="What is your marital status?"
          options={selectOptionsMaritalStatus}
        />
        <RffBooleanToggleField
          required
          size={6}
          name="borrowerHasSSN"
          label="Do you have a US issued Social Security Number?"
        />
        <RffConditional field="borrowerHasSSN" value={true}>
          <RffSsnField
            required
            name="borrowerSSN"
            label="Social Security Number"
          />
        </RffConditional>
      </PublicFormGrid>
    </PublicFormSection>

    <PublicFormSection
      title="Loan Details"
      description="Pick a product and purpose first — the rest of the application adapts to them."
    >
      <PublicFormGrid>
        {/* Both are required: every later step gates its questions on them, so
            leaving either blank produces steps with nothing on them. */}
        <RffSelectToggleField
          required
          name="loanProductType"
          label="Loan Product Type"
          options={selectOptionsLoanProductType}
        />
        <RffSelectToggleField
          required
          name="loanPurpose"
          label="Loan Purpose"
          options={selectOptionsLoanPurpose}
        />
        <RffDateField
          name="expectedClosingDate"
          label="What is the expected closing date for the loan?"
        />
        {/* Product-scoped options. Renders nothing for products where exit
            strategy doesn't apply (DSCR / Other). */}
        <RffExitStrategyField size={6} />

        <RffBooleanToggleField
          name="brokerDeal"
          size={6}
          label="Working with a third-party broker?"
        />
      </PublicFormGrid>
    </PublicFormSection>

    <PublicFormSection
      title="Photo Identification"
      description="A driver's license or passport. Make sure all four corners are visible and the text is legible."
    >
      <PublicFormGrid>
        <RffFileUploadField
          required
          label="Upload a clear copy of your government issued identification"
          docGroup="Borrower Documents"
          docType="Photo Identification"
        />
      </PublicFormGrid>
    </PublicFormSection>
  </Flx>
);

export default StepBorrowerDetails;
